<template>
  <div
    class="sample-questions-popup"
    :class="{ 'is-visible': isVisible, 'dark-theme': isDarkTheme }"
    role="dialog"
    aria-labelledby="popup-title"
    aria-modal="true"
    v-if="isVisible">
    <v-card
      :style="{
        backgroundColor: theme.global.current.value.colors.surface,
        color: theme.global.current.value.colors.botText,
        '--hover-bg': hoverBgColor
      }">
      <div class="popup-content">
        <button
          @click="$emit('close')"
          @keydown.enter="$emit('close')"
          @keydown.space.prevent="$emit('close')"
          class="close-btn"
          :class="{ 'dark-theme': isDarkTheme }"
          aria-label="Close suggested questions panel"
          tabindex="0">
          <v-tooltip activator="parent">
            {{ $t('message.sample_question_container') }}
          </v-tooltip>
          <font-awesome-icon :icon="['fas', 'circle-xmark']" aria-hidden="true" />
        </button>

        <h3 id="popup-title" class="suggested-questions-title">
          <font-awesome-icon :icon="['fas', 'lightbulb']" class="title-icon" aria-hidden="true" />
          {{ $t('message.suggested_questions') }}
        </h3>

        <div class="instructions" role="note">
          {{ $t('message.navigation_instructions') }}
        </div>

        <ul role="listbox"
            class="questions-list"
            @keydown.up.prevent="navigateQuestions('up')"
            @keydown.down.prevent="navigateQuestions('down')"
            @keydown.escape="$emit('close')">
          <li
            v-for="(question, index) in questions"
            :key="index"
            role="option"
            :aria-selected="focusedIndex === index"
            :class="{
              'dark-theme': isDarkTheme,
              'focused': focusedIndex === index
            }"
            :style="{ color: theme.global.current.value.colors.botText }">
            <button
              class="question-button"
              @click="selectQuestion(question.questionText)"
              @keydown.enter.prevent="selectQuestion(question.questionText)"
              @keydown.space.prevent="selectQuestion(question.questionText)"
              :tabindex="focusedIndex === index ? 0 : -1"
              :ref="el => { if (focusedIndex === index) questionButtons[index] = el }"
              :aria-label="'Select question: ' + question.questionText">
              {{ question.questionText }}
            </button>
          </li>
        </ul>
      </div>
    </v-card>
  </div>
</template>

<script>
import { useTheme } from 'vuetify'
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'

export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true
    },
    questions: {
      type: Array,
      required: true
    }
  },

  emits: ['close', 'select'],

  setup(props, { emit }) {
    const theme = useTheme()
    const focusedIndex = ref(0)
    const questionButtons = ref([])

    const hoverBgColor = computed(() => {
      return theme.global.current.value.dark ? '#404040' : '#f0f0f0'
    })

    const isDarkTheme = computed(() => {
      return theme.global.current.value.dark
    })

    const navigateQuestions = (direction) => {
      if (direction === 'up') {
        focusedIndex.value = focusedIndex.value > 0 ? focusedIndex.value - 1 : props.questions.length - 1
      } else {
        focusedIndex.value = focusedIndex.value < props.questions.length - 1 ? focusedIndex.value + 1 : 0
      }
    }

    const selectQuestion = (questionText) => {
      emit('select', questionText)
    }

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape' && props.isVisible) {
        emit('close')
      }
    }

    // Focus management
    watch(() => props.isVisible, (newValue) => {
      if (newValue) {
        focusedIndex.value = 0
        // Focus first question button after component is mounted
        setTimeout(() => {
          if (questionButtons.value[0]) {
            questionButtons.value[0].focus()
          }
        }, 100)
      }
    })

    // Watch focused index changes
    watch(focusedIndex, (newValue) => {
      if (questionButtons.value[newValue]) {
        questionButtons.value[newValue].focus()
      }
    })

    onMounted(() => {
      document.addEventListener('keydown', handleEscape)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleEscape)
    })

    return {
      hoverBgColor,
      theme,
      isDarkTheme,
      focusedIndex,
      questionButtons,
      navigateQuestions,
      selectQuestion
    }
  }
}
</script>

<style scoped>
.sample-questions-popup {
  position: absolute;
  bottom: 130px;
  right: 20px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  max-height: 400px;
  overflow-y: auto;
  transform: scale(0);
  transform-origin: bottom right;
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
  opacity: 0;
  z-index: 1000;
}

.sample-questions-popup.dark-theme {
  background: #121212;
  border-color: #666666;
  box-shadow: 0 2px 10px rgba(255,255,255,0.1);
}

.sample-questions-popup.is-visible {
  transform: scale(1);
  opacity: 1;
}

.popup-content {
  padding: 15px;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-btn:focus-visible {
  outline: 2px solid #4A90E2;
  outline-offset: 2px;
}

.close-btn.dark-theme {
  color: #e21833;
}

.suggested-questions-title {
  margin: 0 0 15px 0;
  padding-right: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1em;
}

.title-icon {
  color: #888;
}

.instructions {
  font-size: 0.9em;
  color: #666;
  margin-bottom: 15px;
  font-style: italic;
}

.dark-theme .instructions {
  color: #aaa;
}

.questions-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.question-button {
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  background: none;
  border: none;
  font-family: 'interstate', sans-serif;
  font-size: 14px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.question-button:hover,
.question-button:focus-visible {
  background-color: var(--hover-bg);
}

.dark-theme .question-button:hover,
.dark-theme .question-button:focus-visible {
  background-color: #4B4B4D;
}

.focused .question-button {
  background-color: var(--hover-bg);
  outline: 2px solid #4A90E2;
  outline-offset: -2px;
}

.dark-theme .focused .question-button {
  background-color: #4B4B4D;
}
</style>
